
import { useRef, useEffect, useCallback, useMemo } from 'react';
import { logger } from '@/utils/production-logger';

interface StateCoordinationConfig {
  componentName: string;
  onError?: (error: Error, context: any) => void;
  onPerformanceIssue?: (operation: string, duration: number) => void;
  cleanupTimeout?: number;
}

interface StateCoordinationReturn {
  registerCleanup: (cleanupFn: () => void) => void;
  unregisterCleanup: (cleanupFn: () => void) => void;
  trackOperation: (operation: string) => { finish: (success?: boolean) => void };
  handleError: (error: Error, context?: any) => void;
  performanceMonitor: (operation: string, threshold?: number) => { finish: () => void };
}

export const useStateCoordination = (
  config: StateCoordinationConfig
): StateCoordinationReturn => {
  // ==================== ALL REFS AT TOP LEVEL ====================
  const cleanupFunctionsRef = useRef<Set<() => void>>(new Set());
  const activeOperationsRef = useRef<Map<string, number>>(new Map());
  const performanceThresholds = useRef<Map<string, number>>(new Map());

  // ==================== STABLE CALLBACKS - TOP LEVEL ====================
  const registerCleanup = useCallback((cleanupFn: () => void) => {
    cleanupFunctionsRef.current.add(cleanupFn);
  }, []);

  const unregisterCleanup = useCallback((cleanupFn: () => void) => {
    cleanupFunctionsRef.current.delete(cleanupFn);
  }, []);

  const trackOperation = useCallback((operation: string) => {
    const operationId = `${operation}-${Date.now()}`;
    const startTime = performance.now();
    
    activeOperationsRef.current.set(operationId, startTime);

    return {
      finish: (success: boolean = true) => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        activeOperationsRef.current.delete(operationId);

        // Check for performance issues
        const threshold = performanceThresholds.current.get(operation) || 5000; // 5s default
        if (duration > threshold && config.onPerformanceIssue) {
          config.onPerformanceIssue(operation, duration);
        }
      }
    };
  }, [config]);

  const handleError = useCallback((error: Error, context?: any) => {
    if (config.onError) {
      config.onError(error, context);
    }
  }, [config]);

  const performanceMonitor = useCallback((operation: string, threshold: number = 3000) => {
    const startTime = performance.now();
    
    // Set custom threshold for this operation
    performanceThresholds.current.set(operation, threshold);

    return {
      finish: () => {
        const duration = performance.now() - startTime;
        
        if (duration > threshold && config.onPerformanceIssue) {
          config.onPerformanceIssue(operation, duration);
        }
      }
    };
  }, [config]);

  // ==================== CLEANUP EFFECT - TOP LEVEL ====================
  useEffect(() => {
    return () => {
      const timeout = config.cleanupTimeout || 5000;

      // Create cleanup promise with timeout
      const cleanupPromise = Promise.allSettled(
        Array.from(cleanupFunctionsRef.current).map(cleanupFn => 
          Promise.resolve().then(() => {
            try {
              cleanupFn();
            } catch (error) {
              // Silent cleanup errors to prevent console spam
            }
          })
        )
      );

      // Timeout for cleanup
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Cleanup timeout')), timeout)
      );

      Promise.race([cleanupPromise, timeoutPromise])
        .finally(() => {
          // Force clear all refs
          cleanupFunctionsRef.current.clear();
          activeOperationsRef.current.clear();
          performanceThresholds.current.clear();
        });
    };
  }, [config.cleanupTimeout]);

  // ==================== MEMOIZED RETURN VALUE ====================
  return useMemo(() => ({
    registerCleanup,
    unregisterCleanup,
    trackOperation,
    handleError,
    performanceMonitor
  }), [registerCleanup, unregisterCleanup, trackOperation, handleError, performanceMonitor]);
};
